import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import type { WorkspaceFile } from './workspace-files';

// Client data layer for the per-chat Files panel — every file in the conversation, newest first.
// Replaces deriving the list from the messages on screen, which could only ever see the loaded
// window (newest 200 rows).
//
// orion-web's CLIENT bundle must not import common-lib, so the shape lives here as a client-local
// contract matching what the route returns.
export interface ConversationAttachment {
  fileName: string;
  mimeType: string;
  /** Empty when the file is a workspace artifact carried by `vfsPath` instead. */
  url: string;
  vfsPath?: string;
  size?: number;
  durationMs?: number;
  /** The message that carried the file, so a caller can navigate to it. */
  messageId: string;
  createdDate?: string;
}

async function fetchConversationAttachments(conversationId: string): Promise<ConversationAttachment[]> {
  const res = await apiFetch(`${API_BASE}/api/conversations/${encodeURIComponent(conversationId)}/attachments`);
  if (!res.ok) {
    throw new Error(`attachments ${res.status}`);
  }
  const body = (await res.json()) as { attachments?: ConversationAttachment[] };
  return body.attachments ?? [];
}

/** Disabled until there is a conversation to ask about — a never-sent chat has no id. */
export function useConversationAttachments(conversationId: string | null | undefined) {
  const query = useQuery({
    queryKey: queryKeys.conversationAttachments(conversationId ?? undefined),
    queryFn: () => fetchConversationAttachments(conversationId as string),
    enabled: Boolean(conversationId),
  });

  return {
    attachments: query.data ?? [],
    // React Query 4 reports a disabled query as loading; with no conversation there is nothing to wait for.
    loading: Boolean(conversationId) && query.isLoading,
    error: query.error ? String(query.error) : null,
  };
}

/**
 * One attachment as the `WorkspaceFile` shape the `artifact-file.ts` open/share helpers take —
 * `content` holds the address. A workspace artifact has an empty `url`, so its `vfsPath` travels
 * instead.
 */
export function attachmentFile(attachment: ConversationAttachment): WorkspaceFile {
  return { name: attachment.fileName, isApp: false, content: attachment.vfsPath || attachment.url };
}
